import { PosthogEventType } from "@repo/app-types/client/client";
import { Color } from "@repo/app-types/constants/colors";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import {
  getInterviewLinkForProject,
  getProjectAndJobTypeProperty,
  JobTypeProperty,
  ProjectMode,
} from "@repo/app-types/entities/project";
import { ProjectDetailsTabPath } from "@repo/app-types/helpers/adminAppRoutes";
import { isRecruitingModeProject } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Pill } from "@repo/ui/Pills/pill";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconAlertTriangle, IconRocket } from "@tabler/icons-react";
import copy from "copy-to-clipboard";
import { useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { Outlet, useNavigate } from "react-router";
import { getAxiosInstanceWithAuth } from "../api/axiosConfig.js";
import { appPaths } from "../app/appPaths.js";
import { Tab, Tabs } from "../components/projectDetailsTabs.js";
import { captureAnalyticsEvent } from "../features/analytics/analyticsClient.js";
import { fetchAssessmentById } from "../features/assessments/assessmentsSlice.js";
import {
  selectCompany,
  selectIsCompanyBlocked,
} from "../features/company/companySlice.js";
import { ImportContactsSlideover } from "../features/contacts/importSlideover.js";
import { InsightsTabContainer } from "../features/insights/insightsTabContainer.js";
import { InterviewsTab } from "../features/interviews/interviewsTab.js";
import { DeleteProjectModal } from "../features/projects/deleteProjectModal.js";
import { InterviewLinkTipsModal } from "../features/projects/interviewLinkTipsModal.js";
import { ProjectMoreMenu } from "../features/projects/projectMoreMenu.js";
import { ProjectNameEditor } from "../features/projects/projectNameEditor.js";
import { ProjectOwnersWidget } from "../features/projects/projectOwnersWidget.js";
import { RecruitingProjectSettings } from "../features/projects/projectSettings/recruiting/recruitingProjectSettings.js";
import { VoiceModeProjectSettings } from "../features/projects/projectSettings/voiceModeProjectSettings.js";
import {
  deleteProject,
  updateProject,
} from "../features/projects/projectsSlice.js";
import { RecruitingProjectMoreMenu } from "../features/projects/recruitingProjectMoreMenu.js";
import { SurveyModeProjectSettings } from "../features/projects/surveyModeProjectSettings.js";
import { VoiceModeShareTemplatesModal } from "../features/projects/voiceModeShareTemplatesModal.js";
import { fetchTeammates } from "../features/teammates/teammatesSlice.js";
import { useAppDispatch, useAppSelector } from "../hooks/hook.js";
import { useProjectNavigation } from "../hooks/useProjectNavigation.js";
import type { ProjectDetailsTabsContext } from "./projectDetailsTabRoutes.js";
import { RegenerateInterviewLinkModal } from "./regenerateInterviewLinkModal.js";

interface ProjectDetailsPageProps {
  project: ProjectWithInterviewCount;
}

export const ProjectDetailsPage: FC<ProjectDetailsPageProps> = ({
  project,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { navigateToTab } = useProjectNavigation(project.id);

  const company = useAppSelector(selectCompany);
  const isRecruitingMode = isRecruitingModeProject(project);

  if (!company) {
    throw new Error("Company not found");
  }

  const isCompanyBlocked = useAppSelector(selectIsCompanyBlocked);

  useEffect(() => {
    dispatch(fetchTeammates());
  }, [dispatch]);

  // Eventually the assessment will come from the job type and will always be set.
  const assessmentId = isRecruitingMode
    ? getProjectAndJobTypeProperty(project, JobTypeProperty.AssessmentId)
    : null;

  // Load project assessment when in recruiting mode.
  useEffect(() => {
    if (assessmentId) {
      dispatch(fetchAssessmentById(assessmentId));
    }
  }, [dispatch, assessmentId]);

  /**
   * State.
   */
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState<boolean>(false);
  const [isDeleteWarningModalOpen, setIsDeleteWarningModalOpen] =
    useState<boolean>(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState<boolean>(false);
  const [isSharingTemplatesModalOpen, setIsSharingTemplatesModalOpen] =
    useState<boolean>(false);
  const [isCsvImportSlideoverOpen, setIsCsvImportSlideoverOpen] =
    useState<boolean>(false);
  const [isCsvExportModalOpen, setIsCsvExportModalOpen] = useState(false);
  const [isCsvExportLoading, setIsCsvExportLoading] = useState(false);
  const [isRegenerateLinkModalOpen, setIsRegenerateLinkModalOpen] =
    useState<boolean>(false);

  const { pending_interview_count } = project;

  const interviewLink = getInterviewLinkForProject(
    import.meta.env.VITE_INTERVIEW_APP_BASE_URL,
    company,
    project,
  );

  // If the project is live or if we have any interviews that are not pending, we should render tabs.
  // Otherwise, we'll only show the project configuration page.
  const shouldRenderTabs = project.is_live || project.interview_count > 0;

  // Ref to the interview limit setting so we can scroll to it when the user clicks the warning pill.
  const interviewLimitSettingRef = useRef<HTMLLabelElement>(null);

  const onClickPause = () => {
    setIsPauseModalOpen(true);
  };

  const onConfirmPause = async () => {
    try {
      await dispatch(
        updateProject({
          projectId: project.id,
          changes: { is_live: false },
        }),
      );

      showToast({
        title: `Project paused`,
        variant: ToastVariant.Success,
      });
    } catch (err) {
      console.error("error updating project", err);
      showToast({
        title: `Failed to pause project`,
        variant: ToastVariant.Error,
      });
    }

    setIsPauseModalOpen(false);
  };

  const onConfirmLaunch = async () => {
    // Launch the project. No validation required because the button is disabled otherwise.
    try {
      await dispatch(
        updateProject({
          projectId: project.id,
          changes: { is_live: true },
        }),
      );

      captureAnalyticsEvent(PosthogEventType.ProjectLaunch, {
        project_id: project.id,
        project_name: project.name,
        num_pending_interviews: pending_interview_count,
      });

      showToast({
        title: isRecruitingMode
          ? `Job opening successfully launched`
          : `Project successfully launched`,
        variant: ToastVariant.Success,
      });
    } catch (err) {
      console.error("error updating project", err);
      showToast({
        title: `Failed to launch project`,
        variant: ToastVariant.Error,
      });
    }

    setIsLaunchModalOpen(false);
  };

  const onConfirmDelete = async () => {
    try {
      // First navigate back to the projects page before deleting so
      // that we don't try to a project that doesn't exist.
      navigate(appPaths.home.build());

      // Hack to prevent race condition where the ProjectsTable lists projects before the delete is complete.
      setTimeout(() => {
        void dispatch(deleteProject(project.id));
      }, 2000);

      showToast({
        title: `Project deleted`,
        variant: ToastVariant.Success,
      });
    } catch (err) {
      console.error("error updating project", err);
    }

    setIsDeleteWarningModalOpen(false);
  };

  const handleCsvExport = async () => {
    setIsCsvExportLoading(true);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.get(`/projects/${project.id}/csv-export`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${project.name}_interviews.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showToast({
        title: "CSV export successful",
        variant: ToastVariant.Success,
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      showToast({
        title: "Failed to export CSV",
        variant: ToastVariant.Error,
      });
    } finally {
      setIsCsvExportLoading(false);
      setIsCsvExportModalOpen(false);
    }
  };

  const renderLaunchButton = () => {
    const launchErrorMessage = computeLaunchErrorMessage();

    const launchButton = (
      <Button
        onClick={() => {
          setIsLaunchModalOpen(true);
        }}
        variant={ButtonVariant.Primary}
        isDisabled={Boolean(launchErrorMessage)}
      >
        Launch
      </Button>
    );

    if (!launchErrorMessage) return launchButton;

    // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
    return (
      <TooltipTrigger>
        <TooltipHoverTarget shape="rounded">{launchButton}</TooltipHoverTarget>
        <SimpleTooltip>{launchErrorMessage}</SimpleTooltip>
      </TooltipTrigger>
    );
  };

  // TODO - handle different CTA scenarios based on project status.
  const renderActionButtons = () => {
    return (
      <>
        {project.is_live && project.mode === ProjectMode.SURVEY ? (
          <Button onClick={onClickPause} variant={ButtonVariant.Secondary}>
            Pause
          </Button>
        ) : null}
        {!project.is_live ? renderLaunchButton() : null}
        {isRecruitingMode ? (
          <RecruitingProjectMoreMenu
            project={project}
            onClickDeleteProject={() => {
              setIsDeleteWarningModalOpen(true);
            }}
            onClickCsvImport={() => {
              setIsCsvImportSlideoverOpen(true);
            }}
            onClickCsvExport={() => {
              setIsCsvExportModalOpen(true);
            }}
            onOpenSharingTemplatesModal={() => {
              setIsSharingTemplatesModalOpen(true);
            }}
            onClickRegenerateInterviewLink={() => {
              setIsRegenerateLinkModalOpen(true);
            }}
          />
        ) : (
          <ProjectMoreMenu
            project={project}
            onClickPause={
              project.is_live && project.mode !== ProjectMode.SURVEY
                ? onClickPause
                : undefined
            }
            onClickCopyInterviewLink={
              project.mode === ProjectMode.SURVEY
                ? () => {
                    copy(interviewLink);
                    setIsSharingTemplatesModalOpen(true);
                  }
                : undefined
            }
            onClickViewDialInstructions={
              project.mode === ProjectMode.VOICE_AGENT
                ? () => {
                    setIsSharingTemplatesModalOpen(true);
                  }
                : undefined
            }
            onClickDeleteProject={() => {
              setIsDeleteWarningModalOpen(true);
            }}
            onClickCsvExport={() => {
              setIsCsvExportModalOpen(true);
            }}
            onClickRegenerateInterviewLink={() => {
              setIsRegenerateLinkModalOpen(true);
            }}
          />
        )}
      </>
    );
  };

  const maybeRenderInterviewLimitWarning = () => {
    if (!project.has_reached_interview_limit) return null;

    return (
      <div className="mx-auto mt-4 w-full max-w-4xl">
        <Pill
          size={Size.MEDIUM}
          color={Color.Red}
          label={
            <div className="flex space-x-2">
              <IconAlertTriangle
                className="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
              <span>
                Monthly interview limit reached.{" "}
                <span
                  className="cursor-pointer font-semibold"
                  onClick={() => {
                    interviewLimitSettingRef.current?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                >
                  Increase this project's limit
                </span>{" "}
                to conduct more interviews.
              </span>
            </div>
          }
        />
      </div>
    );
  };

  const onSaveProject = async (
    changes: ProjectUpdate,
    hidesNotifications = false,
  ) => {
    try {
      await dispatch(
        updateProject({
          projectId: project.id,
          changes,
        }),
      );

      if (!hidesNotifications) {
        showToast({
          title: isRecruitingMode
            ? `Job opening successfully updated`
            : `Project successfully updated`,
          variant: ToastVariant.Success,
        });
      }
    } catch (err) {
      console.error("error updating project", err);
      showToast({
        title: `Failed to update project`,
        variant: ToastVariant.Error,
      });
    }
  };

  const getProjectSettings = (): ReactNode => {
    if (isRecruitingMode) {
      return (
        <RecruitingProjectSettings
          project={project}
          onSaveProject={onSaveProject}
          company={company}
          onClickSharingTemplates={() => {
            setIsSharingTemplatesModalOpen(true);
          }}
        />
      );
    }

    if (project.mode === ProjectMode.VOICE_AGENT) {
      return (
        <VoiceModeProjectSettings
          project={project}
          onSaveProject={onSaveProject}
          company={company}
          onClickSharingTemplates={() => {
            setIsSharingTemplatesModalOpen(true);
          }}
        />
      );
    }

    return (
      <SurveyModeProjectSettings
        project={project}
        company={company}
        onSaveProject={onSaveProject}
        setCurrentTab={navigateToTab}
        interviewLimitSettingRef={interviewLimitSettingRef}
        onCopyInterviewLink={() => {
          setIsSharingTemplatesModalOpen(true);
        }}
      />
    );
  };

  const renderTabs = () => {
    return (
      <Tabs>
        <Tab to={ProjectDetailsTabPath.ProjectConfiguration}>Configuration</Tab>
        <Tab to={ProjectDetailsTabPath.Interviews}>Interviews</Tab>
        <Tab to={ProjectDetailsTabPath.Insights}>Insights</Tab>
      </Tabs>
    );
  };

  // The tab routes live in the router (see the `/project/:projectId` subtree);
  // this variant supplies its tabs' content through the outlet context.
  const tabsContext: ProjectDetailsTabsContext = {
    configurationTab: (
      <div className="mx-auto w-full max-w-4xl">
        <div className="-mx-4 h-full space-y-6 overflow-y-auto p-4">
          {getProjectSettings()}
        </div>
      </div>
    ),
    interviewsTab: <InterviewsTab project={project} />,
    insightsTab: <InsightsTabContainer project={project} />,
    defaultTabPath: ProjectDetailsTabPath.ProjectConfiguration,
  };

  const computeLaunchErrorMessage = () => {
    if (!project.questions.length) {
      return "At least one question is required before launching your project.";
    }

    if (isCompanyBlocked)
      return "An active subscription is required to launch a project - please upgrade to continue.";

    return undefined;
  };

  return (
    <>
      <div className="grid h-full min-h-full w-full grid-rows-[auto_1fr] justify-items-center">
        <div className="w-full px-4">
          <div className="mx-auto w-full max-w-4xl border-b border-border-light">
            <div className="flex min-h-[52px] items-center justify-between">
              <div className="flex flex-row items-center space-x-2">
                <ProjectNameEditor
                  name={project.name}
                  onChange={(newName) => onSaveProject({ name: newName }, true)}
                />
                {shouldRenderTabs ? renderTabs() : null}
              </div>
              <div className="flex items-center space-x-3">
                <ProjectOwnersWidget
                  teammateIds={project.owner_teammate_ids}
                  onTeammateIdsSave={(teammateIds) =>
                    onSaveProject({ owner_teammate_ids: teammateIds }, true)
                  }
                />
                {renderActionButtons()}
              </div>
            </div>
          </div>
        </div>
        <div className="relative h-full w-full overflow-auto px-4">
          {maybeRenderInterviewLimitWarning()}
          <Outlet context={tabsContext} />
        </div>
      </div>

      <SimpleModal
        isOpen={isLaunchModalOpen}
        variant={SimpleModalVariant.Standard}
        icon={
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <IconRocket className="h-6 w-6 text-blue-600" aria-hidden="true" />
          </div>
        }
        title={isRecruitingMode ? "Launch job opening" : "Launch project"}
        subtitle={
          <div className="text-left">
            {isRecruitingMode
              ? `• This job opening will be enabled and candidates will be able to complete interviews`
              : `• This project's interview link (${interviewLink}) will be enabled: you can send it to participants you want to interview`}
            <br />
            {pending_interview_count > 0 && (
              <>
                {`• Outreach emails will be sent to ${pending_interview_count} participant${
                  pending_interview_count > 1 ? "s" : ""
                }.`}
                <br />
              </>
            )}
            {project.incentive_quantity
              ? `• Each participant that completes an interview will receive a $${project.incentive_quantity} gift card`
              : ""}
          </div>
        }
        confirmButtonText="Launch"
        onCancel={() => {
          setIsLaunchModalOpen(false);
        }}
        onConfirm={onConfirmLaunch}
      />

      <SimpleModal
        isOpen={isPauseModalOpen}
        variant={SimpleModalVariant.Warning}
        title="Pause project"
        subtitle={`If you pause this project, any in progress interviews will be cancelled and you'll need to relaunch this project to run more interviews.`}
        confirmButtonText="Pause project"
        onCancel={() => {
          setIsPauseModalOpen(false);
        }}
        onConfirm={onConfirmPause}
      />

      <DeleteProjectModal
        isOpen={isDeleteWarningModalOpen}
        onClose={() => setIsDeleteWarningModalOpen(false)}
        projectId={project.id}
        interviewAgentType={project.settings?.interview_agent_type}
      />

      <RegenerateInterviewLinkModal
        isOpen={isRegenerateLinkModalOpen}
        onClose={() => {
          setIsRegenerateLinkModalOpen(false);
        }}
        projectId={project.id}
        inviteLinkUrl={interviewLink}
      />
      {project.mode === ProjectMode.VOICE_AGENT ? (
        <VoiceModeShareTemplatesModal
          isOpen={isSharingTemplatesModalOpen}
          onClose={() => {
            setIsSharingTemplatesModalOpen(false);
          }}
          project={project}
          company={company}
        />
      ) : (
        <InterviewLinkTipsModal
          isOpen={isSharingTemplatesModalOpen}
          incentiveQuantity={project.incentive_quantity}
          link={interviewLink}
          onClose={() => {
            setIsSharingTemplatesModalOpen(false);
          }}
          numQuestions={project.questions.length}
        />
      )}

      <ImportContactsSlideover
        isOpen={isCsvImportSlideoverOpen}
        defaultProjectId={project.id}
        projectTypeFilter="recruiting"
        onClose={() => {
          setIsCsvImportSlideoverOpen(false);
        }}
      />

      <SimpleModal
        isOpen={isCsvExportModalOpen}
        variant={SimpleModalVariant.Standard}
        onCancel={() => {
          setIsCsvExportModalOpen(false);
        }}
        title="Export interviews to CSV"
        subtitle="Interview data, transcripts, and insights for this project will be exported to a CSV. This may take up to a minute. "
        confirmButtonText="Continue"
        isPrimaryButtonLoading={isCsvExportLoading}
        onConfirm={handleCsvExport}
      />
    </>
  );
};
